"use client"

import { ArrowUp, CheckCircle2, Cookie, Mail } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectOption } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { FooterCurrency, FooterLocale, FooterNewsletter } from "./footer-types"

export function FooterNewsletterIsland({ newsletter }: { newsletter: FooterNewsletter }) {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const invalid = status === "error"

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setStatus("error")
      return
    }
    setStatus("loading")
    window.setTimeout(() => setStatus("success"), 550)
  }

  return (
    <form action={newsletter.action} onSubmit={handleSubmit} noValidate className="grid gap-3">
      {newsletter.heading && <h2 className="font-heading text-2xl font-semibold tracking-tight">{newsletter.heading}</h2>}
      {newsletter.supportingLine && <p className="text-sm leading-6 text-current/68">{newsletter.supportingLine}</p>}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="grid flex-1 gap-2 text-sm font-semibold">
          <span>Email address</span>
          <Input type="email" value={email} onChange={(event) => { setEmail(event.target.value); if (invalid) setStatus("idle") }} placeholder="you@example.com" aria-describedby={invalid ? "footer-newsletter-error" : undefined} className="h-12 rounded-full bg-background text-foreground" />
        </label>
        <Button type="submit" className="h-12 rounded-full" disabled={status === "loading"}>
          {status === "loading" ? "Sending" : newsletter.submitLabel ?? "Subscribe"}
          {status === "success" ? <CheckCircle2 className="size-4" /> : <Mail className="size-4" />}
        </Button>
      </div>
      <p id="footer-newsletter-error" className={cn("min-h-5 text-xs font-medium", invalid ? "text-destructive" : "text-current/58")} aria-live="polite">
        {invalid ? "Enter a valid email address." : status === "success" ? newsletter.successMessage ?? "You are subscribed." : newsletter.microcopy ?? "No spam. Unsubscribe anytime."}
      </p>
    </form>
  )
}

export function FooterBackToTopIsland() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const update = () => setVisible(window.scrollY > 640)
    update()
    window.addEventListener("scroll", update, { passive: true })
    return () => window.removeEventListener("scroll", update)
  }, [])
  if (!visible) return null
  return <button type="button" aria-label="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-5 right-5 z-50 grid size-11 place-items-center rounded-full bg-primary text-primary-foreground shadow-2xl"><ArrowUp className="size-5" /></button>
}

export function FooterLocaleCurrencyIsland({ locales = [], currencies = [] }: { locales?: FooterLocale[]; currencies?: FooterCurrency[] }) {
  const [locale, setLocale] = useState(locales[0]?.value ?? "")
  const [currency, setCurrency] = useState(currencies[0]?.value ?? "")

  function persist(name: string, value: string) {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; samesite=lax`
    window.localStorage.setItem(name, value)
    window.dispatchEvent(new CustomEvent(`${name}:change`, { detail: value }))
  }

  if (!locales.length && !currencies.length) return null
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      {locales.length > 0 && <label className="grid gap-1 text-xs font-semibold text-current/72"><span>Language</span><Select value={locale} onChange={(event) => { setLocale(event.target.value); persist("hiveku_locale", event.target.value) }} className="min-w-36 rounded-full"><>{locales.map((item) => <SelectOption key={item.value} value={item.value}>{item.label}</SelectOption>)}</></Select></label>}
      {currencies.length > 0 && <label className="grid gap-1 text-xs font-semibold text-current/72"><span>Currency</span><Select value={currency} onChange={(event) => { setCurrency(event.target.value); persist("hiveku_currency", event.target.value) }} className="min-w-32 rounded-full"><>{currencies.map((item) => <SelectOption key={item.value} value={item.value}>{item.label}</SelectOption>)}</></Select></label>}
    </div>
  )
}

export function FooterCookieSettingsIsland({ label = "Cookie settings" }: { label?: string }) {
  return <button type="button" className="inline-flex items-center gap-2 text-sm text-current/64 transition-colors hover:text-current" onClick={() => window.dispatchEvent(new CustomEvent("hiveku:open-cookie-settings"))}><Cookie className="size-4" />{label}</button>
}
